// Pivot dimensions for the Report model, expressed in EQL terms.
//
// Each dimension carries the three names EQL needs, because the contract uses
// a different notation depending on where the field appears:
//   field       -> goes in `fields` (raw mode): dot / bracket notation
//   groupBy     -> goes in `aggregate.group_by`: same notation as `field`
//   filterField -> goes in `filters`: double-underscore notation
//   responseKey -> the key the backend uses in the raw-row response
//
// `aggregatable: false` means the dimension is derived client-side (year /
// month come from created_at) and therefore cannot be a server group_by.

export const DASH = "—";

export const FIXED_DIMENSION_DEFS = [
  {
    key: "status",
    titleKey: "builder.f_status",
    field: "status.current",
    responseKey: "status.current",
    groupBy: "status.current",
    filterField: "status__current",
    aggregatable: true,
  },
  {
    key: "entryway",
    titleKey: "builder.f_channel",
    field: "entryway",
    responseKey: "entryway",
    groupBy: "entryway",
    filterField: "entryway",
    aggregatable: true,
  },
  {
    key: "severity",
    titleKey: "builder.f_severity",
    field: "severity",
    responseKey: "severity",
    groupBy: "severity",
    filterField: "severity",
    aggregatable: true,
  },
  {
    // Closure type for closed reports (founded / unfounded / undefined /
    // inadmissible). Absent on not-yet-closed reports -> "—" bucket.
    key: "closed_type",
    titleKey: "builder.f_closed_type",
    field: "status.details.closed",
    responseKey: "status.details.closed",
    groupBy: "status.details.closed",
    filterField: "status__details__closed",
    aggregatable: true,
  },
  {
    key: "business_unit_origin",
    titleKey: "builder.f_business_unit",
    field: "business_unit_origin",
    responseKey: "business_unit_origin",
    groupBy: "business_unit_origin",
    filterField: "business_unit_origin",
    aggregatable: true,
  },
  {
    key: "year",
    titleKey: "builder.f_year",
    field: "created_at",
    responseKey: "created_at",
    groupBy: null, // derived client-side
    filterField: "created_at",
    aggregatable: false,
    derive: "year",
  },
  {
    key: "month",
    titleKey: "builder.f_month",
    field: "created_at",
    responseKey: "created_at",
    groupBy: null, // derived client-side
    filterField: "created_at",
    aggregatable: false,
    derive: "month",
  },
];

/**
 * Builds a pivot dimension from an entry of GET /reports/eql/catalogues/.
 * `full_key` is the literal metadata key (e.g. "C::OWN::business_units"),
 * so the addressable field is `metadata[<full_key>]`.
 */
export const buildCatalogDimension = (catalogue) => {
  const fullKey = catalogue.full_key;
  const field = `metadata[${fullKey}]`;
  return {
    key: `cat:${fullKey}`,
    title: catalogue.label || catalogue.field_label || catalogue.key,
    field,
    responseKey: field, // backend keys extraction results by the original string
    groupBy: field,
    filterField: null, // filters reject metadata[...]; use jsonb_contains on `metadata`
    aggregatable: true,
    isCatalog: true,
    // Only multi-level catalogs have depth to truncate; drives the depth stepper.
    hierarchical: !!catalogue.hierarchical,
    fullKey,
    // Alias + origin locate this catalog's options (with their locales) in
    // getAllUsedCatalogs, so stored value keys can be shown as localized labels.
    catalogueKey: catalogue.key,
    source: catalogue.source, // "own" | "custom" | "unknown"
  };
};

/**
 * The key getAllUsedCatalogs() files a catalog under: origin + alias.
 * Mirrors the backend (ListLastFormCataloguesView): own_catalogue -> "OWN::",
 * everything else (custom / unknown) -> "EG::".
 */
export const catalogRootKey = (dim) =>
  `${dim.source === "own" ? "OWN" : "EG"}::${dim.catalogueKey}`;

/**
 * Report indicators are booleans nested under `custom_client_info.indicators`.
 * `custom_client_info` is an allowed EQL field, so JSON-path (dot) notation
 * addresses each one for both group_by and raw fields.
 * @param {string} jsonKey - e.g. "has_plan" (no "indicator_" prefix)
 * @param {string} title
 */
export const buildIndicatorDimension = (jsonKey, title, iconName) => {
  const field = `custom_client_info.indicators.${jsonKey}`;
  return {
    key: `ind:${jsonKey}`,
    title,
    field,
    responseKey: field, // backend keys json_path results by the original string
    groupBy: field,
    filterField: `custom_client_info__indicators__${jsonKey}`,
    aggregatable: true,
    isIndicator: true,
    iconName, // reuse the indicator's chosen icon in the chip
  };
};

/**
 * A flag = one tag from catalog TG-100. Reports store selected tags as a string
 * array in `custom_client_info.tags`, so a per-tag dimension is a boolean
 * "has this tag" (Yes/No). Membership is computed client-side from the array,
 * hence not server-aggregatable. `tagKey` also drives the flag's color.
 */
export const buildTagFlagDimension = (tagKey, title) => {
  const field = "custom_client_info.tags";
  return {
    key: `tag:${tagKey}`,
    title,
    field,
    responseKey: field,
    aggregatable: false,
    isTagFlag: true,
    tagKey,
  };
};

/**
 * Mirrors the backend's `_apply_depth` (serializer.py): a hierarchical value
 * is a sequence of `type::value` pairs, so N levels == the first N*2 segments.
 *   "type::meli::type::mexico::type::estado", depth 2 -> "type::meli::type::mexico"
 */
export const applyDepth = (value, depth) => {
  if (typeof value !== "string" || !depth) return value;
  return value.split("::").slice(0, depth * 2).join("::");
};

const capitalize = (s) =>
  typeof s === "string" && s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/** The catalog value keys in a `type::a::type::b` path: the odd segments. */
const catalogValueKeys = (value) =>
  typeof value === "string"
    ? value.split("::").filter((_, i) => i % 2 === 1)
    : [];

/**
 * Fallback renderer: capitalizes the raw value keys with no catalog fetch.
 *   "type::meli::type::mexico" -> "Meli ▸ Mexico"
 * Used when the catalog's option locales aren't available.
 */
export const prettyCatalogValue = (value) => {
  if (typeof value !== "string") return value;
  const keys = catalogValueKeys(value);
  if (!keys.length) return value;
  return keys.map(capitalize).join(" ▸ ");
};

/**
 * Localized renderer: resolves every level of a stored value to its label.
 *   "type::grievance" + {grievance: {locales: {es: "Denuncias"}}} -> "Denuncias"
 *
 * Two sources, tried in order per level:
 *   1. `nodes` — the flat tree from eqlCatalogueTree(), keyed by the CUMULATIVE
 *      value path, so it resolves any depth and cannot confuse a deep key with
 *      a same-named first-level one.
 *   2. `options` — getAllUsedCatalogs()'s first-level map, keyed by bare value
 *      key. It is all there is when the catalog is flat (no tree fetched).
 * Anything still unknown falls back to the capitalized key, as prettyCatalogValue.
 *
 * @param {string} value - stored catalog value ("type::a::type::b")
 * @param {Object} options - first-level map: value key -> { label, locales }
 * @param {(item: Object) => string} getLocalizedLabel - from useLocalizedLabel
 * @param {Object} [nodes] - full tree: "type::a::type::b" -> { label, locales }
 */
export const localizeCatalogValue = (value, options, getLocalizedLabel, nodes) => {
  if (typeof value !== "string") return value;
  const keys = catalogValueKeys(value);
  if (!keys.length) return value;
  let path = "";
  return keys
    .map((k) => {
      path = path ? `${path}::type::${k}` : `type::${k}`;
      const item = nodes?.[path] || options?.[k];
      return item ? getLocalizedLabel(item) : capitalize(k);
    })
    .join(" ▸ ");
};

/**
 * Positive filter on a catalog dimension. `filters` cannot address
 * `metadata[key]`, but `jsonb_contains` on `metadata` does Postgres `@>`
 * containment against the [{key, value}] array.
 * Note: this matches the value EXACTLY, so it will not match a depth-truncated
 * group value.
 */
export const catalogFilter = (fullKey, value) => ({
  field: "metadata",
  operator: "jsonb_contains",
  value: [{ key: fullKey, value }],
});
